import { controller, target, targets } from "@github/catalyst"

// Keeps the region control in sync with the chosen country: countries with a
// state/province list get their <select>, everyone else the free-text input.
// Only the active control is enabled, so only it is submitted. When Mapbox
// autofill retrieves an address it has already filled the text inputs by
// their autocomplete attributes; `retrieved` maps the country and region
// onto the dropdowns, which Mapbox cannot fill itself. The feature's
// `country_code` is ISO alpha-2 (lowercase) and `address_level1` is the
// region's full name ("California"), so states are matched by option text.
interface MapboxRetrieveEvent extends Event {
  detail?: {
    features?: Array<{
      properties?: { country_code?: string; address_level1?: string }
    }>
  }
}

@controller
class AddressFormElement extends HTMLElement {
  @target country!: HTMLSelectElement
  @target regionText!: HTMLInputElement
  @targets regionSelects!: HTMLSelectElement[]
  @target autofill: HTMLElement | undefined

  // The submit listener lives on the enclosing <form>, which is outside this
  // element, so it is attached here and removed on disconnect.
  private form: HTMLFormElement | null = null
  private onSubmit = () => this.restoreNames()

  connectedCallback() {
    this.form = this.closest("form")
    this.form?.addEventListener("submit", this.onSubmit)
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.onSubmit)
    this.form = null
  }

  // Mapbox appends " address-search" to the bound input's name attribute to
  // suppress browser autofill; put the Rails param name back before submit.
  restoreNames() {
    if (!this.autofill) return
    for (const input of Array.from(this.autofill.getElementsByTagName("input"))) {
      const name = input.getAttribute("name")
      if (name && name.endsWith(" address-search")) input.setAttribute("name", name.replace(/ address-search$/, ""))
    }
  }

  countryChanged() {
    const select = this.regionSelectFor(this.country.value)
    for (const candidate of this.regionSelects) this.activate(candidate, candidate === select)
    this.activate(this.regionText, select === null)
  }

  retrieved(event: MapboxRetrieveEvent) {
    const properties = event.detail?.features?.[0]?.properties
    if (!properties) return

    const countryCode = (properties.country_code || "").toUpperCase()
    if (countryCode && this.hasOption(this.country, countryCode)) this.country.value = countryCode
    this.countryChanged()

    const select = this.regionSelectFor(this.country.value)
    const region = (properties.address_level1 || "").trim()
    if (select) {
      const wanted = region.toLowerCase()
      const option = Array.from(select.options).find((o) => o.value.toLowerCase() === wanted || o.text.toLowerCase() === wanted)
      select.value = option ? option.value : ""
    } else {
      this.regionText.value = region
    }

    // Mapbox moves focus to address-line2 after dispatching `retrieve` (and
    // leaves its suggestion list open while the address line is focused), so
    // a tick later drop focus entirely: the list closes and nothing jumps.
    setTimeout(() => {
      const active = document.activeElement
      if (active instanceof HTMLElement && this.contains(active)) active.blur()
    }, 0)
  }

  private regionSelectFor(countryCode: string): HTMLSelectElement | null {
    return this.regionSelects.find((select) => select.getAttribute("data-country") === countryCode) || null
  }

  private activate(control: HTMLSelectElement | HTMLInputElement, active: boolean) {
    control.hidden = !active
    control.disabled = !active
  }

  private hasOption(select: HTMLSelectElement, value: string): boolean {
    return Array.from(select.options).some((option) => option.value === value)
  }
}

export default AddressFormElement
